import type { EmployeeAllergyDto } from "@/generated/model/employeeAllergyDto";
import { useProfileSectionEditing } from "@/hooks/use-profile-section-form";
import { textOrNotRecorded } from "@/lib/people";

import { EditAllergiesForm } from "../forms/EditAllergiesForm";
import { ProfileSectionCard } from "../ProfileSectionCard";
import { ProfileSectionEditButton } from "../ProfileSectionEditButton";

interface AllergiesSectionProps {
  nurseryId: string;
  userId: string;
  allergies: EmployeeAllergyDto[];
  canEdit: boolean;
}

export function AllergiesSection({
  nurseryId,
  userId,
  allergies,
  canEdit,
}: AllergiesSectionProps) {
  const { editing, editButtonRef, startEditing, stopEditing } =
    useProfileSectionEditing();

  return (
    <ProfileSectionCard
      title="Allergies"
      testId="profile-section-allergies"
      action={
        canEdit &&
        !editing && (
          <ProfileSectionEditButton
            ref={editButtonRef}
            section="allergies"
            onClick={startEditing}
          />
        )
      }
      editForm={
        canEdit &&
        editing && (
          <EditAllergiesForm
            nurseryId={nurseryId}
            userId={userId}
            allergies={allergies}
            onDone={stopEditing}
          />
        )
      }
    >
      <AllergyList allergies={allergies} />
    </ProfileSectionCard>
  );
}

export function AllergyList({
  allergies,
}: {
  allergies: EmployeeAllergyDto[];
}) {
  if (allergies.length === 0) {
    return (
      <span className="text-body text-subtext-color">
        No allergies recorded
      </span>
    );
  }
  return (
    <ul className="flex w-full flex-col gap-3">
      {allergies.map((allergy, index) => (
        <li
          key={`${allergy.allergen}-${index}`}
          className="flex flex-col gap-0.5"
        >
          <span className="text-body-bold font-body-bold text-default-font break-words">
            {allergy.allergen}
          </span>
          <span className="text-body text-subtext-color whitespace-pre-wrap break-words">
            {textOrNotRecorded(allergy.severityNotes)}
          </span>
        </li>
      ))}
    </ul>
  );
}
